import { AxiosRequestConfig } from 'axios';
import {
  api as generatedApi,
  TasksControllerGetTasksStateApiArg,
  TasksControllerGetTasksStateApiResponse,
  MediaControllerUploadDocumentApiArg,
  MediaControllerUploadDocumentApiResponse,
  PageRo,
  SettingDto,
  SettingRo,
  TaskRo,
} from './api.generated';
import type {
  UncertainRegionDecisionRo,
  UncertainRegionsRo,
} from 'src/utils/uncertainRegions';

/**
 * `Setting.uncertainRegions` ("not sure" markers): the feature toggle plus
 * thresholds. Hand-written until the next `yarn syncApis`. A setting without
 * the key reads as enabled.
 */
export type UncertainRegionsSetting = {
  enabled: boolean;
  handwritingLabelEnabled: boolean;
  lowConfidenceMax: number;
  unreadableConfidenceMax: number;
  bucketLowMax: number;
  minAreaPct: number;
  mergeGapPct: number;
};

export type SettingWithUncertainRegions = (SettingDto | SettingRo) & {
  uncertainRegions?: Partial<UncertainRegionsSetting> | null;
};

/** Organisation retention period in days. Null on the API means off. */
export type RetentionDays = 30 | 90 | 365;

/** GET/PATCH /companies/retention. */
export type CompanyRetention = {
  days: RetentionDays | null;
  effectiveFrom: string | null;
  updatedBy: string | null;
  updatedAt: string | null;
  canEdit: boolean;
};

/** GET /companies/retention/preview: counts only, never names. */
export type CompanyRetentionPreview = {
  tasks: number;
  pages: number;
  oldestCreatedAt: string | null;
  firstRunAt: string;
  confirm: string;
};

/**
 * Trash row: why the task is in the trash when no person put it there, and
 * whether a permanent delete has started (then it cannot be restored).
 */
export type TaskRoWithDeletedReason = TaskRo & {
  deletedReason?: 'retention' | null;
  purgePending?: boolean;
};

/** Task list row with the cached count of open markers (absent = 0). */
export type TaskRoWithMarkers = TaskRo & { openMarkerCount?: number };

/**
 * Workspace setting "Tagging model" (Setting.taggingModel). Hand-written
 * until the next `yarn syncApis`. Missing reads as "standard".
 */
export type TaggingModel = 'standard' | 'precise';

export type SettingWithTaggingModel = (SettingDto | SettingRo) & {
  taggingModel?: TaggingModel | null;
};

/** Task with the tagging model fields (absent on older API responses). */
export type TaskRoWithTaggingModel = TaskRoWithMarkers & {
  setting: SettingWithTaggingModel;
  /** GLiNER models that tagged the pages. */
  taggerModels?: string[];
  /** Mean OCR confidence over the tagged pages (0-100), null if unknown. */
  ocrConfidence?: number | null;
  /** Review started: "process again with precise" asks to confirm first (single-task GET only). */
  reviewStarted?: boolean;
  /** Reviewer decisions a confirmed re-run clears; manual redactions are kept (single-task GET only). */
  reviewDecisionCount?: number;
  /** Why "process again with precise" is refused for good (a hard 409 reason), null if possible (single-task GET only). */
  rerunBlockReason?: RerunBlockReason | null;
  /** Failed pages "Retry failed pages" would queue again now; 0 when refused (single-task GET only). */
  retryablePageCount?: number;
};

/** Reasons in the 409 of POST tasks/:taskId/retry-failed-pages. */
export type RetryFailedConflictReason =
  | 'not-failed'
  | 'verified'
  | 'output-generated'
  | 'retry-cap-reached'
  | 'nothing-to-retry'
  | 'task-changed'
  | (string & {});

export type RetryFailedPagesRo = {
  taskId: string;
  /** Pages queued for AI again. */
  retried: number;
  pages: number[];
  skippedCap: number;
  skippedOutput: number;
  state: string;
};

/** Reasons in the 409 of POST tasks/:taskId/rerun-precise. */
export type RerunBlockReason =
  | 'not-ready'
  | 'already-precise'
  | 'review-started'
  | 'review-changed'
  | 'rerun-in-progress'
  | 'verified'
  | 'output-generated'
  | (string & {});

export type TasksRerunPreciseArgs = {
  taskId: string;
  /** The reviewer confirmed that their decisions are cleared. */
  confirmReset?: boolean;
  /** The decision count the confirm dialog showed. */
  expectedDecisionCount?: number;
};

/** Page with the model that tagged it (absent on older API responses). */
export type PageRoWithTaggerModel = PageRo & { taggerModel?: string | null };

export type TaggingRerunRo = {
  taskId: string;
  taggingModel: TaggingModel;
  pages: number;
};

/** Answer of "remove all suggestions on this page". */
export type PageSuggestionsRemoval = {
  batchId: string;
  itemIds: string[];
  count: number;
};

enum ApiTagsEnum {
  USER = 'USER',
  WORKSPACE = 'WORKSPACE',
  WORKSPACE_USER = 'WORKSPACE_USER',
  FOLDER = 'FOLDER',
  COMPANY = 'COMPANY',
  TASK = 'TASK',
  NOTIFICATION = 'NOTIFICATION',
  WORD = 'WORD',
  LEGAL_CODE = 'LEGAL_CODE',
  USER_KEY = 'USER_KEY',
  UNCERTAIN_REGION = 'UNCERTAIN_REGION',
}

type tagsType =
  | ApiTagsEnum
  | {
      type: ApiTagsEnum;
      id: string;
    };

type tagGeneratorType = <T>(props: {
  tag: ApiTagsEnum;
  list?: T;
  idKey?: string;
  callbackFunc?: (arg: T) => tagsType[];
}) => tagsType[];

const tagGenerator: tagGeneratorType = ({
  tag,
  list,
  idKey = 'id',
  callbackFunc,
}) => {
  if (!list || !Array.isArray(list)) {
    console.warn(
      `Please fix the result of 'tagGenerator' function in api.ts file.\n\n tag name is "${tag}"\n\n list: ${list}\n\n ATTENTION: 'tagGenerator' function just be used in case of the response be an array and has id in each item.\n in other cases you can get help from 'callbackFunc' to generate result`
    );
    return [];
  }
  if (callbackFunc) {
    return callbackFunc(list);
  }
  const tags = list.map((item) => ({
    type: tag,
    id: item[idKey],
  }));
  return [...tags, tag];
};

export const api = generatedApi
  .enhanceEndpoints({
    addTagTypes: Object.values(ApiTagsEnum),
    endpoints: {
      // ===================== NOTIFICATION ===================== //
      notificationControllerGetUnseenNotification: {
        providesTags: (response) =>
          tagGenerator({ list: response, tag: ApiTagsEnum.NOTIFICATION }),
      },
      notificationControllerGetPreviewUnseenNotification: {
        providesTags: (response) =>
          tagGenerator({ list: response, tag: ApiTagsEnum.NOTIFICATION }),
      },
      notificationControllerGetNotification: {
        providesTags: (response) =>
          tagGenerator({ list: response, tag: ApiTagsEnum.NOTIFICATION }),
      },
      notificationControllerGetUnseenNotificationCount: {
        providesTags: [
          {
            type: ApiTagsEnum.NOTIFICATION,
            id: 'unSeenNotifCount',
          },
        ],
      },
      notificationControllerGetAllNotification: {
        providesTags: [
          {
            type: ApiTagsEnum.NOTIFICATION,
            id: 'unSeenNotifCount',
          },
        ],
      },
      notificationControllerSeenNotification: {
        invalidatesTags: [ApiTagsEnum.NOTIFICATION],
      },
      notificationControllerCreateNotification: {
        invalidatesTags: [ApiTagsEnum.NOTIFICATION],
      },
      notificationControllerCreateMyCompanyNotification: {
        invalidatesTags: [ApiTagsEnum.NOTIFICATION],
      },
      notificationControllerCreateCompanyNotification: {
        invalidatesTags: [ApiTagsEnum.NOTIFICATION],
      },
      // ======================= WORKSPACE ======================= //
      workspacesControllerGetMyWorkspaces: {
        providesTags: (response) =>
          tagGenerator({
            list: response,
            tag: ApiTagsEnum.WORKSPACE,
          }),
      },
      workspacesControllerGetWorkspaceById: {
        providesTags: (response) =>
          response ? [{ type: ApiTagsEnum.WORKSPACE, id: response.id }] : [],
      },
      workspacesControllerGetCompanyWorkspaces: {
        providesTags: (response) =>
          tagGenerator({ list: response, tag: ApiTagsEnum.WORKSPACE }),
      },

      workspacesControllerCreateWorkspace: {
        invalidatesTags: [ApiTagsEnum.WORKSPACE],
      },
      workspacesControllerDeleteWorkspace: {
        invalidatesTags: [ApiTagsEnum.WORKSPACE],
      },
      workspacesControllerUpdateWorkspace: {
        invalidatesTags: [ApiTagsEnum.WORKSPACE],
      },
      // ======================= WORKSPACE_USER ======================= //
      workspacesControllerFindWorkspaceUser: {
        providesTags: [ApiTagsEnum.WORKSPACE_USER],
      },

      workspacesControllerInviteUserToWorkspace: {
        invalidatesTags: [ApiTagsEnum.WORKSPACE_USER, ApiTagsEnum.WORKSPACE],
      },
      workspacesControllerDeleteWorkspaceUser: {
        invalidatesTags: [ApiTagsEnum.WORKSPACE_USER, ApiTagsEnum.WORKSPACE],
      },
      workspacesControllerAssignWorkspaceRole: {
        invalidatesTags: [ApiTagsEnum.WORKSPACE_USER, ApiTagsEnum.WORKSPACE],
      },
      // ======================= FOLDER ======================= //
      workspacesControllerGetFolderById: {
        providesTags: (response) =>
          response
            ? [
                ...response.folders.map(({ id }) => ({
                  type: ApiTagsEnum.FOLDER,
                  id: id,
                })),
                ...response.tasks.map(({ id }) => ({
                  type: ApiTagsEnum.TASK,
                  id: id,
                })),
                ApiTagsEnum.FOLDER,
                ApiTagsEnum.TASK,
              ]
            : [ApiTagsEnum.FOLDER, ApiTagsEnum.TASK],
      },
      workspacesControllerGetRootFolder: {
        providesTags: (response) =>
          response
            ? [
                ...response.folders.map(({ id }) => ({
                  type: ApiTagsEnum.FOLDER,
                  id,
                })),
                ...response.tasks.map(({ id }) => ({
                  type: ApiTagsEnum.TASK,
                  id,
                })),
                ApiTagsEnum.FOLDER,
                ApiTagsEnum.TASK,
              ]
            : [ApiTagsEnum.FOLDER, ApiTagsEnum.TASK],
      },

      workspacesControllerCreateNewFolder: {
        invalidatesTags: [ApiTagsEnum.FOLDER],
      },
      workspacesControllerRemoveFolder: {
        invalidatesTags: [ApiTagsEnum.FOLDER],
      },
      workspacesControllerDeleteFolder: {
        invalidatesTags: [ApiTagsEnum.FOLDER],
      },
      workspacesControllerRenameFolder: {
        invalidatesTags: [ApiTagsEnum.FOLDER],
      },
      workspacesControllerAddFolderTo: {
        invalidatesTags: [ApiTagsEnum.FOLDER],
      },
      // ======================= COMPANIES ======================= //
      usersControllerGetCompanies: {
        providesTags: (response) =>
          tagGenerator({ list: response, tag: ApiTagsEnum.COMPANY }),
      },
      // ======================= USERS ======================= //
      usersControllerFind: {
        providesTags: (response) =>
          tagGenerator({ list: response, tag: ApiTagsEnum.USER }),
      },
      usersControllerFindCompanyUsers: {
        providesTags: (response) =>
          tagGenerator({ list: response, tag: ApiTagsEnum.USER }),
      },
      usersControllerGetUserProfile: {
        providesTags: (response) =>
          tagGenerator({
            list: response,
            tag: ApiTagsEnum.USER,
            idKey: 'username',
          }),
      },
      // TODO
      usersControllerCheckResetPasswordCred: {},
      usersControllerGetCompanyUsers: {
        providesTags: (response) =>
          tagGenerator({
            list: response,
            tag: ApiTagsEnum.USER,
            idKey: 'username',
          }),
      },

      usersControllerUpdate: {
        invalidatesTags: [ApiTagsEnum.COMPANY, ApiTagsEnum.USER],
      },
      usersControllerCreate: {
        invalidatesTags: [ApiTagsEnum.USER, ApiTagsEnum.COMPANY],
      },
      usersControllerCreateCompanyUser: {
        invalidatesTags: [ApiTagsEnum.USER, ApiTagsEnum.COMPANY],
      },
      usersControllerSuspend: {
        invalidatesTags: [ApiTagsEnum.USER],
      },
      usersControllerActivate: {
        invalidatesTags: [ApiTagsEnum.USER],
      },
      usersControllerAssignRole: {
        invalidatesTags: [ApiTagsEnum.USER],
      },
      usersControllerUpdateCompanyLicense: {
        invalidatesTags: (
          response,
          error,
          { updateCompanyLimitDto: { id } }
        ) => [{ type: ApiTagsEnum.COMPANY, id }],
      },
      usersControllerDelete: {
        invalidatesTags: [ApiTagsEnum.USER],
      },
      usersControllerUpdateUserProfile: {
        invalidatesTags: [ApiTagsEnum.USER],
      },
      usersControllerMailResetPassword: {
        invalidatesTags: [ApiTagsEnum.USER],
      },
      usersControllerResetPassword: {
        invalidatesTags: [ApiTagsEnum.USER],
      },
      usersControllerChangePassword: {
        invalidatesTags: [ApiTagsEnum.USER],
      },
      // ========================= TASK ========================= //
      tasksControllerGetTask: {
        providesTags: (response) =>
          response
            ? [{ type: ApiTagsEnum.TASK, id: response.id }, ApiTagsEnum.TASK]
            : [],
      },
      tasksControllerGetCompanyTasks: {
        providesTags: (response) =>
          tagGenerator({ list: response, tag: ApiTagsEnum.TASK }),
      },
      tasksControllerGetLastModifiedTasks: {
        providesTags: (response) =>
          tagGenerator({ list: response, tag: ApiTagsEnum.TASK }),
      },
      // TODO
      tasksControllerGetTasksState: {},
      tasksControllerGetTaskOutputState: {},
      tasksControllerGetTaskCurrentPage: {
        providesTags: () => [ApiTagsEnum.TASK],
      },
      tasksControllerGetTaskPagesUrl: {},
      tasksControllerGetTaskOutFileUrl: {},

      tasksControllerDelete: {
        invalidatesTags: (response, error, { idDto: { id } }) => [
          { type: ApiTagsEnum.TASK, id },
        ],
      },
      tasksControllerDeleteItem: {
        invalidatesTags: (response, error, { deleteItemDto: { id } }) => [
          { type: ApiTagsEnum.TASK, id },
        ],
      },
      tasksControllerRemoveMany: {
        invalidatesTags: (response, error, { idsDto: { ids } }) =>
          ids.map((id) => ({ type: ApiTagsEnum.TASK, id })),
      },
      mediaControllerUploadDocument: {
        invalidatesTags: () => [ApiTagsEnum.TASK],
      },
      tasksControllerSetAssignee: {
        invalidatesTags: (response, error, { assignTaskDto: { id } }) => [
          { type: ApiTagsEnum.TASK, id },
          ApiTagsEnum.TASK,
          ApiTagsEnum.FOLDER,
          ApiTagsEnum.NOTIFICATION,
        ],
      },
      tasksControllerRemove: {
        invalidatesTags: (response, error, { idDto: { id } }) => [
          { type: ApiTagsEnum.TASK, id },
          ApiTagsEnum.TASK,
        ],
      },
      tasksControllerRestore: {
        // A task from a deleted workspace moves into another one.
        invalidatesTags: () => [
          ApiTagsEnum.TASK,
          ApiTagsEnum.FOLDER,
          ApiTagsEnum.WORKSPACE,
        ],
      },
      tasksControllerGetMyRemovedTasks: {
        providesTags: (response) =>
          [
            // No response on an error (older backends 404 an empty trash).
            ...tagGenerator({ list: response ?? [], tag: ApiTagsEnum.TASK }),
            ApiTagsEnum.TASK,
          ],
      },
      tasksControllerGetArchive: {
        providesTags: (response) =>
          [
            ...tagGenerator({
              list: response?.items,
              tag: ApiTagsEnum.TASK,
            }),
            ApiTagsEnum.TASK,
          ],
      },
      tasksControllerDeleteCompanyTasks: {
        invalidatesTags: () => [ApiTagsEnum.TASK],
      },
      tasksControllerAddItem: {},
      tasksControllerAddCustomBox: {},
      tasksControllerUpdateBox: {},
      tasksControllerChangeRedactState: {},
      tasksControllerChangeVerify: {},
      tasksControllerGenerateOutput: {},
      tasksControllerSetTaskCurrentPage: {},
      tasksControllerCreateLegalCode: {},
      tasksControllerRemoveLegalCode: {},
      tasksControllerRedactAll: {},
      tasksControllerUnRedactAll: {},
      tasksControllerChangeItemsRedactState: {},
      tasksInternalControllerGetPdf2ImgTask: {},
      tasksInternalControllerSetPdf2ImgTaskResult: {},
      tasksInternalControllerGetGeneratingTasks: {},
      tasksInternalControllerSetGeneratingTasks: {},
      ...(process.env.NODE_ENV === 'production'
        ? {
            authControllerLogin: {
              invalidatesTags: Object.values(ApiTagsEnum),
            },
          }
        : {}),
      // ======================= ROLE ======================= //
      // rolesControllerReadPermissions: {},
      rolesControllerFindRole: {},
      rolesControllerReadRole: {},

      rolesControllerCreateRole: {},
      rolesControllerUpdateRole: {},
      rolesControllerDeleteRole: {},
      // ======================= WORD_TAGGER ======================= //
      wordTaggerControllerFindAll: {
        providesTags: (response) =>
          tagGenerator({ list: response, tag: ApiTagsEnum.WORD }),
      },
      wordTaggerControllerFindMyWords: {
        providesTags: (response) =>
          tagGenerator({ list: response, tag: ApiTagsEnum.WORD }),
      },
      wordTaggerControllerFindOne: {
        providesTags: (response) =>
          response ? [{ id: response.id, type: ApiTagsEnum.WORD }] : [],
      },

      wordTaggerControllerCreate: {
        invalidatesTags: () => [ApiTagsEnum.WORD],
      },
      wordTaggerControllerUpdate: {
        invalidatesTags: () => [ApiTagsEnum.WORD],
      },
      wordTaggerControllerRemove: {
        invalidatesTags: () => [ApiTagsEnum.WORD],
      },
      // ======================= LEGAL_CODES ======================= //
      legalCodeControllerFindMy: {
        providesTags: (response) =>
          tagGenerator({ list: response, tag: ApiTagsEnum.LEGAL_CODE }),
      },
      legalCodeControllerCreate: {
        invalidatesTags: () => [ApiTagsEnum.LEGAL_CODE],
      },
      legalCodeControllerUpdate: {
        invalidatesTags: () => [ApiTagsEnum.LEGAL_CODE],
      },
      legalCodeControllerSetDefault: {
        invalidatesTags: () => [ApiTagsEnum.LEGAL_CODE],
      },
      legalCodeControllerRemove: {
        invalidatesTags: () => [ApiTagsEnum.LEGAL_CODE],
      },
      usersControllerGetNotOwnerUsers: {
        providesTags: (response) => [ApiTagsEnum.COMPANY],
      },
      companiesControllerFindAll: {
        providesTags: (response) =>
          tagGenerator({
            list: response,
            tag: ApiTagsEnum.COMPANY,
          }),
      },
      companiesControllerCreate: {
        invalidatesTags: () => [ApiTagsEnum.COMPANY],
      },
      companiesControllerDelete: {
        invalidatesTags: () => [ApiTagsEnum.COMPANY, ApiTagsEnum.USER],
      },
    },
  })
  .injectEndpoints({
    endpoints: (build) => ({
      createBillingPortalSession: build.mutation<
        { url: string },
        { returnUrl?: string } | void
      >({
        query: (body) => ({
          url: `/api/v1/billing/portal`,
          method: 'POST',
          body: body || {},
        }),
      }),
      tasksControllerGetTasksState_custom: build.mutation<
        TasksControllerGetTasksStateApiResponse,
        TasksControllerGetTasksStateApiArg
      >({
        query: (queryArg) => {
          let response = '/api/v1/tasks/tasks-state';
          queryArg.ids.forEach((id, index) => {
            if (index === 0) {
              response = response + `?ids=${id}`;
            } else {
              response = response + `&ids=${id}`;
            }
            return response;
          });

          return {
            url: response,
          };
        },
      }),
      // Process one task again with the precise model. Hand-written until
      // the next `yarn syncApis`.
      tasksRerunPrecise: build.mutation<TaggingRerunRo, TasksRerunPreciseArgs>({
        query: ({ taskId, confirmReset, expectedDecisionCount }) => {
          const body = {
            ...(confirmReset !== undefined ? { confirmReset } : {}),
            ...(expectedDecisionCount !== undefined
              ? { expectedDecisionCount }
              : {}),
          };
          return {
            url: `/api/v1/tasks/${taskId}/rerun-precise`,
            method: 'POST',
            // Without a confirmation: no body, as before.
            ...(Object.keys(body).length ? { body } : {}),
            // RerunPreciseSuggestion shows its own localized toast or dialog
            // for every failure.
            silentErrors: true,
          };
        },
        invalidatesTags: (_res, _err, { taskId }) => [
          { type: ApiTagsEnum.TASK, id: taskId },
          { type: ApiTagsEnum.UNCERTAIN_REGION, id: taskId },
        ],
      }),
      // Queue the failed pages of one task again (owner, company owner or manager, or admin).
      // Hand-written until the next `yarn syncApis`.
      tasksRetryFailedPages: build.mutation<
        RetryFailedPagesRo,
        { taskId: string }
      >({
        query: ({ taskId }) => ({
          url: `/api/v1/tasks/${taskId}/retry-failed-pages`,
          method: 'POST',
          // RetryFailedPages shows its own localized toast for every failure.
          silentErrors: true,
        }),
        invalidatesTags: (_res, _err, { taskId }) => [
          { type: ApiTagsEnum.TASK, id: taskId },
        ],
      }),
      // "Not sure" markers. Hand-written until the next `yarn syncApis`.
      uncertainRegionsList: build.query<UncertainRegionsRo, { taskId: string }>(
        {
          query: ({ taskId }) => ({
            url: `/api/v1/tasks/${taskId}/uncertain-regions`,
          }),
          providesTags: (_res, _err, { taskId }) => [
            { type: ApiTagsEnum.UNCERTAIN_REGION, id: taskId },
          ],
        }
      ),
      uncertainRegionsRedact: build.mutation<
        UncertainRegionDecisionRo,
        { taskId: string; markerId: string; legalCode?: string }
      >({
        query: (body) => ({
          url: '/api/v1/tasks/uncertain-regions/redact',
          method: 'PUT',
          body,
        }),
      }),
      uncertainRegionsComplete: build.mutation<
        UncertainRegionDecisionRo,
        { taskId: string; markerId: string; itemIds: string[] }
      >({
        query: (body) => ({
          url: '/api/v1/tasks/uncertain-regions/complete',
          method: 'PUT',
          body,
        }),
      }),
      uncertainRegionsDismiss: build.mutation<
        UncertainRegionDecisionRo,
        { taskId: string; markerId: string }
      >({
        query: (body) => ({
          url: '/api/v1/tasks/uncertain-regions/dismiss',
          method: 'PUT',
          body,
        }),
      }),
      uncertainRegionsReopen: build.mutation<
        UncertainRegionDecisionRo,
        { taskId: string; markerIds: string[]; removeItems?: boolean }
      >({
        query: (body) => ({
          url: '/api/v1/tasks/uncertain-regions/reopen',
          method: 'PUT',
          body,
        }),
      }),
      uncertainRegionsBulk: build.mutation<
        UncertainRegionDecisionRo,
        {
          taskId: string;
          markerIds: string[];
          decision: 'redacted' | 'dismissed';
          scope: 'page' | 'below_bucket' | 'all';
          legalCode?: string;
        }
      >({
        query: (body) => ({
          url: '/api/v1/tasks/uncertain-regions/bulk',
          method: 'PUT',
          body,
        }),
      }),
      // "Remove all suggestions on this page". Hand-written until the next
      // `yarn syncApis`.
      tasksPageSuggestionsCount: build.query<
        { count: number },
        { taskId: string; pageNumber: number }
      >({
        query: ({ taskId, pageNumber }) => ({
          url: `/api/v1/tasks/${taskId}/pages/${pageNumber}/suggestions-count`,
        }),
        keepUnusedDataFor: 0,
      }),
      tasksRemovePageSuggestions: build.mutation<
        PageSuggestionsRemoval,
        { taskId: string; pageNumber: number }
      >({
        query: ({ taskId, pageNumber }) => ({
          url: `/api/v1/tasks/${taskId}/pages/${pageNumber}/remove-suggestions`,
          method: 'POST',
        }),
      }),
      tasksRestorePageSuggestions: build.mutation<
        { itemIds: string[]; count: number },
        { taskId: string; batchId: string; itemIds: string[] }
      >({
        query: ({ taskId, batchId, itemIds }) => ({
          url: `/api/v1/tasks/${taskId}/restore-suggestions`,
          method: 'POST',
          body: { batchId, itemIds },
        }),
      }),
      getUserKeyStatus: build.query<{ active: boolean }, void>({
        query: () => ({ url: '/api/v1/user-keys' }),
        providesTags: [ApiTagsEnum.USER_KEY],
      }),
      createUserKey: build.mutation<{ userKey: string }, void>({
        query: () => ({
          url: '/api/v1/user-keys',
          method: 'POST',
        }),
        invalidatesTags: [ApiTagsEnum.USER_KEY],
      }),
      revokeUserKey: build.mutation<void, void>({
        query: () => ({
          url: '/api/v1/user-keys',
          method: 'DELETE',
        }),
        invalidatesTags: [ApiTagsEnum.USER_KEY],
      }),
      // Organisation document retention. Hand-written until the next
      // `yarn syncApis`.
      companyRetentionGet: build.query<CompanyRetention, void>({
        query: () => ({ url: '/api/v1/companies/retention' }),
        providesTags: [{ type: ApiTagsEnum.COMPANY, id: 'retention' }],
      }),
      companyRetentionPreview: build.query<CompanyRetentionPreview, { days: RetentionDays }>({
        query: ({ days }) => ({
          url: '/api/v1/companies/retention/preview',
          params: { days },
        }),
        keepUnusedDataFor: 0,
      }),
      companyRetentionUpdate: build.mutation<
        CompanyRetention,
        { days: RetentionDays | null; confirm?: string }
      >({
        query: (body) => ({
          url: '/api/v1/companies/retention',
          method: 'PATCH',
          body,
        }),
        invalidatesTags: [{ type: ApiTagsEnum.COMPANY, id: 'retention' }],
      }),
      customMediaControllerUploadDocument: build.mutation<
        MediaControllerUploadDocumentApiResponse,
        {
          body: Omit<MediaControllerUploadDocumentApiArg['body'], 'workspaceId'> & {
            workspaceId?: string;
          };
          isDemoUpload?: boolean;
          abortController: AbortController;
          onUploadProgress: AxiosRequestConfig['onUploadProgress'];
        }
      >({
        query: ({ body, isDemoUpload, abortController, onUploadProgress }) => {
          const formData = new FormData();
          const excludedDemoFields = ['workspaceId', 'folderId', 'setting'];

          Object.keys(body).forEach((k) => {
            if (isDemoUpload && excludedDemoFields.includes(k)) return;

            const key = k as keyof typeof body;
            let value = body[key];

            if (value === undefined || value === null) return;

            if (
              key === 'setting' ||
              key === 'haveLegalCode' ||
              key === 'pagesRanges'
            ) {
              value = JSON.stringify(value);
            }

            formData.append(key, value as string | Blob);
          });

          return {
            url: isDemoUpload ? `/api/v1/demo/upload` : `/api/v1/media`,
            method: 'POST',
            body: formData,
            abortController,
            onUploadProgress,
            // The upload page retries 5xx and shows one toast (with the
            // file name) for every final failure, 4xx included.
            silentErrors: true,
          };
        },
        invalidatesTags: () => [ApiTagsEnum.TASK, ApiTagsEnum.FOLDER],
      }),
    }),
  });

export const {
  useCompanyRetentionGetQuery,
  useLazyCompanyRetentionPreviewQuery,
  useCompanyRetentionUpdateMutation,
  useTasksRerunPreciseMutation,
  useTasksRetryFailedPagesMutation,
  useUncertainRegionsListQuery,
  useUncertainRegionsRedactMutation,
  useUncertainRegionsCompleteMutation,
  useUncertainRegionsDismissMutation,
  useUncertainRegionsReopenMutation,
  useUncertainRegionsBulkMutation,
  useTasksPageSuggestionsCountQuery,
  useTasksRemovePageSuggestionsMutation,
  useTasksRestorePageSuggestionsMutation,
  useCreateBillingPortalSessionMutation,
  useGetUserKeyStatusQuery,
  useCreateUserKeyMutation,
  useRevokeUserKeyMutation,
  useLazyTasksControllerGetTaskOutputStateQuery,
  useTasksControllerGetTasksState_customMutation,
  useTasksControllerGetTaskProgressMutation,
  useLazyTasksControllerSearchQuery,
  useCustomMediaControllerUploadDocumentMutation,
} = api;
